import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";
import { AlertTriangle, Trash2, Info } from "lucide-react";

/**
 * Reusable confirmation dialog component
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onOpenChange - Function to change open state
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description
 * @param {string} props.confirmText - Text for confirm button
 * @param {string} props.cancelText - Text for cancel button
 * @param {function} props.onConfirm - Function called on confirm
 * @param {string} props.variant - "danger" | "warning" | "info"
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title = "Sei sicuro?",
  description = "Questa azione non può essere annullata.",
  confirmText = "Conferma",
  cancelText = "Annulla",
  onConfirm,
  variant = "danger",
  loading = false,
  requireText = null,
  details = null,
}) {
  const [typed, setTyped] = useState("");

  const typedOk = !requireText || typed.trim().toUpperCase() === String(requireText).toUpperCase();

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const icons = {
    danger: <Trash2 className="h-5 w-5 text-red-500" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  };

  const buttonStyles = {
    danger: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
    warning: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
    info: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {icons[variant]}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {details ? (
          <div className="text-sm text-muted-foreground space-y-1">{details}</div>
        ) : null}
        {requireText ? (
          <div className="space-y-1 pt-2">
            <label className="text-xs font-medium" htmlFor="confirm-typed-input">
              Per confermare, scrivi <span className="font-mono font-bold">{requireText}</span>
            </label>
            <input
              id="confirm-typed-input"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireText}
              autoComplete="off"
              data-testid="confirm-typed-input"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading || !typedOk}
            className={buttonStyles[variant]}
          >
            {loading ? "Attendere..." : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Delete confirmation dialog - preset for delete operations
 */
export function DeleteConfirmDialog({
  open,
  onOpenChange,
  itemName,
  onConfirm,
  loading = false,
  description = null,
  requireText = null,
  details = null,
}) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Conferma Eliminazione"
      description={description || `Sei sicuro di voler eliminare "${itemName}"? Questa azione non può essere annullata.`}
      confirmText="Elimina"
      cancelText="Annulla"
      onConfirm={onConfirm}
      variant="danger"
      loading={loading}
      requireText={requireText}
      details={details}
    />
  );
}

export default ConfirmDialog;
